import React, { useState, useEffect, useRef } from 'react';
import { Box, Flex, Text } from 'rebass';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import theme from 'styled-theming';
import { Section, Toggle, Loading } from '../components';
import api from '../api';
import { useAppState, useNotification } from '../hooks';

const textColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.midLightGrey,
});
const ToggleLabel = styled(Text)`
  color: ${textColor};
  font-size: 18px;
`;

function ServerToServerToggle() {
  const { accountId: brandId, themeMode } = useAppState();
  const { addNotification } = useNotification();
  const [serverToServerEnabled, setServerToServerEnabled] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    async function fetchServerToServerAuthState() {
      try {
        const isEnabled = await api.mavenConfig.getServerToServerAuthStatus(brandId);
        if (isMounted.current) {
          setServerToServerEnabled(isEnabled);
        }
      } catch (error) {
        if (isMounted.current) {
          addNotification(error, 'error');
        }
      }
    }

    fetchServerToServerAuthState();
    return () => {
      isMounted.current = false;
    };
  }, [addNotification, brandId]);

  async function setEnabledAndMakeApiCall(value) {
    // Store orignal value in case api call fails
    const originalValue = serverToServerEnabled;
    // set new value so that the UI responds quicker
    setServerToServerEnabled(value);
    try {
      await api.mavenConfig.setServerToServerAuthStatus(brandId, value);
    } catch (error) {
      if (isMounted.current) {
        // reset original value on failure
        setServerToServerEnabled(originalValue);
        addNotification(error, 'error');
      }
    }
  }

  async function handleToggleButtonPressed() {
    await setEnabledAndMakeApiCall(!serverToServerEnabled);
  }

  return (
    <Box>
      <Section
        title="Enable Conversation Attributes"
        subtitle="This will create an API key to enable access to the Conversation attributes."
        data-testid={`mis-toggle-${serverToServerEnabled ? 'enabled' : 'disabled'}`}
      >
        {serverToServerEnabled === null ? <Loading size={24} data-testid="loading-mis-status" /> : (
          <Flex alignItems="center">
            <Toggle
              className={themeMode === 'light' ? null : 'dark-theme'}
              icons={false}
              checked={serverToServerEnabled}
              onChange={handleToggleButtonPressed}
              data-testid="mis-toggle"
            />
            <ToggleLabel
              data-testid="mis-label"
              variant="label"
              ml={2}
              fontSize={1}
              fontWeight={300}
              color={serverToServerEnabled ? 'success' : 'midGrey'}
              onClick={(event) => event.stopPropagation()}
            >
              {`${serverToServerEnabled ? 'enabled' : 'disabled'}`}
            </ToggleLabel>
          </Flex>
        )}
      </Section>
    </Box>
  );
}

export default ServerToServerToggle;
